import { NAuthClientError } from '@nauth-toolkit/client';
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import {
  confirmPasswordReset,
  fetchProfile,
  requestPasswordReset,
  respondToPasswordChallenge,
  signInWithPassword,
  signOut,
  type AuthProfile,
  type PasswordChallengeInput,
  type PasswordCredentials,
  type ResetPasswordInput,
} from './api';

/**
 * Auth state, read from the server through TanStack Query.
 *
 * There is no AuthContext here on purpose. The QueryClient already *is* the
 * shared context: every component calling `useAuth()` reads one cache entry, one
 * request is in flight no matter how many callers there are, and invalidating
 * `queryKeys.profile()` updates all of them. A context wrapping `useState` would
 * be a second copy of server state, which is exactly what this app forbids.
 */

const FIVE_MINUTES = 5 * 60_000;

export const profileQueryOptions = queryOptions({
  queryKey: queryKeys.profile(),
  queryFn: ({ signal }) => fetchProfile(signal),
  staleTime: FIVE_MINUTES,
  // A 401 is the *answer* to "who am I" — not a transient failure. Retrying it
  // three times just delays the redirect to /signin by a second.
  retry: false,
});

export interface AuthState {
  readonly profile: AuthProfile | null;
  readonly user: AuthProfile['user'] | null;
  /** True only while the first profile fetch is in flight. */
  readonly isLoading: boolean;
  readonly isAuthenticated: boolean;
  /** The server answered 401, as opposed to the request failing outright. */
  readonly isUnauthenticated: boolean;
  /** Signed in with nauth, but Noorix has not created a Rundown account for this person. */
  readonly isAccountNotSetUp: boolean;
  /** A real failure — network down, API 500 — as distinct from "not signed in". */
  readonly error: Error | null;
}

export function useAuth(): AuthState {
  const query = useQuery(profileQueryOptions);

  const status = query.error instanceof NAuthClientError ? query.error.statusCode : undefined;
  const unauthenticated = status === 401;
  const accountNotSetUp = status === 403;

  return {
    profile: query.data ?? null,
    user: query.data?.user ?? null,
    isLoading: query.isPending,
    isAuthenticated: query.data !== undefined,
    isUnauthenticated: unauthenticated,
    isAccountNotSetUp: accountNotSetUp,
    error: unauthenticated || accountNotSetUp ? null : (query.error ?? null),
  };
}

export function useSignIn() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: PasswordCredentials) => signInWithPassword(credentials),
    onSuccess: async (response) => {
      // A challenge is a 2xx that signed nobody in, so there is nothing to
      // refetch — the caller branches on `signInOutcome`.
      if (response.challengeName !== undefined) return;

      // Awaited so the caller can navigate into a guarded route without
      // RequireAuth briefly seeing "not signed in" and bouncing back here.
      await queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
}

/** Finish a sign-in that was held up by a forced password change. */
export function useRespondToPasswordChallenge() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: PasswordChallengeInput) => respondToPasswordChallenge(input),
    onSuccess: async (response) => {
      if (response.challengeName !== undefined) return;
      await queryClient.invalidateQueries({ queryKey: queryKeys.profile() });
    },
  });
}

export function useSignOut() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => signOut(),
    onSettled: () => {
      // Clear everything, not just the profile. The cache holds one tenant's
      // requests, and the next person to sign in on this machine must not see
      // them flash on screen before the refetch lands.
      queryClient.clear();
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (identifier: string) => requestPasswordReset(identifier),
  });
}

export function useConfirmPasswordReset() {
  return useMutation({
    mutationFn: (input: ResetPasswordInput) => confirmPasswordReset(input),
  });
}
